import React, {
  createContext, useContext, useState, useCallback, useEffect, useMemo,
} from 'react';
import { loadConsent, saveConsent } from '../utils/consent';
import { loadGA4, unloadGA4 } from '../utils/analyticsLoaders/ga4Loader';
import { loadRB2B, unloadRB2B } from '../utils/analyticsLoaders/rb2bLoader';

/**
 * Global, default-deny consent store.
 *
 * Two categories:
 * - `analytics` — gates GA4 and PostHog (see useAnalyticsConsentBridge for
 *   the PostHog side; GA4 is loaded/unloaded directly below).
 * - `visitorIdentification` — gates RB2B.
 *
 * Until the visitor makes an explicit choice (`consent.decided === false`),
 * every category stays denied and nothing analytics-related runs — this
 * also covers corrupt/old stored consent, which `loadConsent()` fails
 * closed to the same default-deny state.
 */
const ConsentContext = createContext(null);

export const useConsent = () => {
  const ctx = useContext(ConsentContext);
  if (!ctx) {
    throw new Error('useConsent must be used within a ConsentProvider');
  }
  return ctx;
};

export const ConsentProvider = ({ children }) => {
  const [consent, setConsent] = useState(() => loadConsent());
  const [preferencesOpen, setPreferencesOpen] = useState(false);

  // Load/unload GA4 strictly from the current consent value. Both loader
  // functions are idempotent, so this only ever performs the network-side
  // effect that matches the latest decision — never before a decision exists.
  useEffect(() => {
    if (consent.analytics) {
      loadGA4();
    } else {
      unloadGA4();
    }
  }, [consent.analytics]);

  useEffect(() => {
    if (consent.visitorIdentification) {
      loadRB2B();
    } else {
      unloadRB2B();
    }
  }, [consent.visitorIdentification]);

  const applyAndPersist = useCallback((categories) => {
    const record = saveConsent(categories);
    setConsent(record);
    setPreferencesOpen(false);
  }, []);

  /** Grants every optional category. */
  const acceptAll = useCallback(() => {
    applyAndPersist({ analytics: true, visitorIdentification: true });
  }, [applyAndPersist]);

  /** Denies every optional category (essential/app behavior is unaffected). */
  const rejectNonEssential = useCallback(() => {
    applyAndPersist({ analytics: false, visitorIdentification: false });
  }, [applyAndPersist]);

  /** Persists an explicit per-category choice from the Customize panel. */
  const savePreferences = useCallback((categories) => {
    applyAndPersist({
      analytics: !!categories?.analytics,
      visitorIdentification: !!categories?.visitorIdentification,
    });
  }, [applyAndPersist]);

  const openPreferences = useCallback(() => setPreferencesOpen(true), []);
  const closePreferences = useCallback(() => setPreferencesOpen(false), []);

  const value = useMemo(() => ({
    consent,
    // The banner shows until the visitor makes an explicit choice — never
    // implicitly dismissed as acceptance.
    bannerVisible: !consent.decided,
    preferencesOpen,
    acceptAll,
    rejectNonEssential,
    savePreferences,
    openPreferences,
    closePreferences,
  }), [
    consent,
    preferencesOpen,
    acceptAll,
    rejectNonEssential,
    savePreferences,
    openPreferences,
    closePreferences,
  ]);

  return (
    <ConsentContext.Provider value={value}>
      {children}
    </ConsentContext.Provider>
  );
};

export default ConsentContext;
